import React from "react";
import { Link } from "react-router-dom";
import { SEO_LANDING_PAGES } from "../data/seoLandingPages";
import { 
  GraduationCap, 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Shield, 
  Sparkles, 
  CheckCircle2, 
  Award,
  BadgeCheck
} from "lucide-react";

const SEO_DISTRICTS = SEO_LANDING_PAGES
  .filter((p) => p.district)
  .map((p) => ({ path: p.path, district: p.district, slug: p.path.split("/").pop() }));

export default function Footer() {
  return (
    <footer className="bg-[#071E54] text-slate-300 text-sm border-t-4 border-[#FFC107]">
      {/* Top Value Banner */}
      <div className="border-b border-blue-900/60 bg-[#051640] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            <div className="flex items-start space-x-3 p-4 rounded-xl bg-[#0B256B] border border-blue-800">
              <div className="p-2.5 rounded-xl bg-amber-400 text-[#071E54]">
                <GraduationCap className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-white font-bold text-sm">VNIT Alumni Founders</h4>
                <p className="text-xs text-slate-300 mt-0.5">Er. Santosh Yadav (12+ yrs Physics Guru) & Dr. Sowmya Karri Yadav.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3 p-4 rounded-xl bg-[#0B256B] border border-blue-800">
              <div className="p-2.5 rounded-xl bg-blue-500 text-white">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-white font-bold text-sm">Dabba Phone Sanctum</h4>
                <p className="text-xs text-slate-300 mt-0.5">Zero smartphone distraction + Smart study timer & learning tab toolbox.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3 p-4 rounded-xl bg-[#0B256B] border border-blue-800">
              <div className="p-2.5 rounded-xl bg-emerald-500 text-white">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-white font-bold text-sm">Tab-Based Micro Tests</h4>
                <p className="text-xs text-slate-300 mt-0.5">Daily 45-min tests reviewed same evening with physical Error-Log analysis.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3 p-4 rounded-xl bg-[#0B256B] border border-blue-800">
              <div className="p-2.5 rounded-xl bg-amber-500 text-[#071E54]">
                <Award className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-white font-bold text-sm">1:10 Mentorship</h4>
                <p className="text-xs text-slate-300 mt-0.5">Every student known by name, with doubt counters run by the lecture teachers.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Links */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-10">
          
          {/* Brand Col */}
          <div className="xl:col-span-2 space-y-4">
            <Link to="/" data-testid="footer-brand" className="flex items-center space-x-3">
              <div className="relative">
                <div className="absolute -inset-0.5 rounded-xl bg-orange-500/50 blur-sm opacity-70" />
                <div className="relative w-12 h-12 rounded-xl bg-slate-900 ring-1 ring-orange-400 flex items-center justify-center overflow-hidden shadow-md shadow-orange-500/30">
                  <picture>
                    <source
                      type="image/webp"
                      srcSet="/assets/brand/rankers-logo-96.webp 1x, /assets/brand/rankers-logo-192.webp 2x, /assets/brand/rankers-logo-288.webp 3x"
                    />
                    <img
                      src="/assets/brand/rankers-logo-96.png"
                      srcSet="/assets/brand/rankers-logo-96.png 1x, /assets/brand/rankers-logo-192.png 2x, /assets/brand/rankers-logo-288.png 3x"
                      alt="The Rankers"
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                      width="96"
                      height="96"
                    />
                  </picture>
                </div>
              </div>
              <div>
                <span className="font-black text-lg text-white tracking-tight">THE RANKERS ACADEMY</span>
                <p className="text-[10px] text-amber-300 font-bold tracking-wider">PROVEN EXTRA-ORDINARY PERSONAL MENTORSHIP • 12 YEARS</p>
              </div>
            </Link>
            <p className="text-xs leading-relaxed text-slate-300 pr-4">
              “Hard work is the entry ticket. Method is the rank.” We build rankers brick by brick through daily tab micro-tests, dedicated DPPs, and direct founder mentorship at Laxmi Nagar, Nagpur.
            </p>
            <div className="pt-2 flex flex-wrap gap-2">
              <span className="text-xs px-3 py-1 rounded-full bg-blue-900/80 text-blue-200 border border-blue-700 font-semibold">
                Plot 10, Buty Layout, Laxmi Nagar
              </span>
              <span className="text-xs px-3 py-1 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/40 font-bold">
                100% (500/500) Board Scorers
              </span>
            </div>
          </div>

          {/* Programs */}
          <div>
            <h4 className="text-white font-bold text-sm mb-4 tracking-wider uppercase border-b border-blue-800 pb-1">Programs</h4>
            <ul className="space-y-2.5 text-xs">
              <li><Link to="/courses" data-testid="footer-course-jee" className="hover:text-amber-300 transition-colors">JEE Main + Advanced (2-Yr)</Link></li>
              <li><Link to="/courses" data-testid="footer-course-neet" className="hover:text-amber-300 transition-colors">NEET Medical Comprehensive (2-Yr)</Link></li>
              <li><Link to="/courses" data-testid="footer-course-jee-cet" className="hover:text-amber-300 transition-colors">JEE Main + MHT-CET (2-Yr)</Link></li>
              <li><Link to="/courses" data-testid="footer-course-cet" className="hover:text-amber-300 transition-colors">MHT-CET Pure (1-Yr Top 1%)</Link></li>
              <li><Link to="/courses" data-testid="footer-course-repeaters" className="hover:text-amber-300 transition-colors">Repeaters Intensive (Dec Finish)</Link></li>
              <li><Link to="/courses" data-testid="footer-course-foundation" className="hover:text-amber-300 transition-colors">Junior Foundation (Class 8-10 + Free Kit)</Link></li>
            </ul>
          </div>

          {/* Quick Portals */}
          <div>
            <h4 className="text-white font-bold text-sm mb-4 tracking-wider uppercase border-b border-blue-800 pb-1">Quick Portals</h4>
            <ul className="space-y-2.5 text-xs">
              <li><Link to="/rtse" data-testid="footer-rtse" className="text-amber-300 hover:text-amber-200 font-bold">RTSE 3.0 Admit Card & 90% Waiver</Link></li>
              <li><Link to="/demo" data-testid="footer-trial" className="hover:text-amber-300 transition-colors">Sit & Decide (Free 1-Day Class)</Link></li>
              <li><Link to="/toppers" data-testid="footer-results" className="hover:text-amber-300 transition-colors">Hall of Fame & Scorecards</Link></li>
              <li><Link to="/methodology" data-testid="footer-methodology" className="hover:text-amber-300 transition-colors">The 6-Pillar Formula</Link></li>
              <li><Link to="/login" data-testid="footer-login" className="hover:text-amber-300 transition-colors">Student Login</Link></li>
            </ul>
          </div>

          {/* Campus Location */}
          <div>
            <h4 className="text-white font-bold text-sm mb-4 tracking-wider uppercase border-b border-blue-800 pb-1">Our Head Centre</h4>
            <ul className="space-y-3 text-xs">
              <li className="flex items-start space-x-2">
                <MapPin className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <span>Plot 10, Buty Layout, RPTS Road, Laxmi Nagar, Nagpur 440022 (200m from Mate Square)</span>
              </li>
              <li className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-emerald-400 shrink-0" />
                <a data-testid="footer-call" href="tel:7219074788" className="font-bold text-white min-w-0">Call & WhatsApp: 721 907 4788</a>
              </li>
              <li className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-emerald-400 shrink-0" />
                <a data-testid="footer-call-alt" href="tel:8329100890" className="font-semibold text-slate-200 min-w-0">Call: 832 910 0890</a>
              </li>
              <li className="flex items-center space-x-2">
                <Mail className="w-4 h-4 text-cyan-400 shrink-0" />
                <a href="mailto:therankersacademyngp@gmail.com" data-testid="footer-email" className="min-w-0 break-all">therankersacademyngp@gmail.com</a>
              </li>
              <li className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-amber-400 shrink-0" />
                <span>Academy: 8:15 AM - 8:15 PM · Office: 10:00 AM - 8:00 PM</span>
              </li>
            </ul>
          </div>

        </div>

        {/* SEO: coaching-in-Nagpur + Vidarbha district links (crawlable internal links) */}
        <div className="mt-10 pt-6 border-t border-blue-900">
          <h4 className="text-white font-bold text-sm mb-4 tracking-wider uppercase">Coaching in Nagpur &amp; across Vidarbha</h4>
          <div className="flex flex-wrap gap-2 text-xs">
            <Link to="/jee-coaching-in-nagpur" data-testid="footer-seo-jee" className="px-3 py-1 rounded-full bg-blue-900/70 border border-blue-800 hover:text-amber-300 transition-colors">JEE Coaching in Nagpur</Link>
            <Link to="/neet-coaching-in-nagpur" data-testid="footer-seo-neet" className="px-3 py-1 rounded-full bg-blue-900/70 border border-blue-800 hover:text-amber-300 transition-colors">NEET Coaching in Nagpur</Link>
            <Link to="/mht-cet-classes-nagpur" data-testid="footer-seo-cet" className="px-3 py-1 rounded-full bg-blue-900/70 border border-blue-800 hover:text-amber-300 transition-colors">MHT-CET Classes Nagpur</Link>
            {SEO_DISTRICTS.map((d) => (
              <Link key={d.path} to={d.path} data-testid={`footer-seo-${d.slug}`} className="px-3 py-1 rounded-full bg-blue-900/50 border border-blue-800/70 text-slate-300 hover:text-amber-300 transition-colors">
                {d.district} students
              </Link>
            ))}
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-blue-900 space-y-4 text-xs text-slate-400">
          <div className="grid gap-2 text-slate-300">
            <a href="https://wa.me/917219074788?text=RTSE" target="_blank" rel="noreferrer" data-testid="footer-wa-contact" className="inline-flex items-center gap-2 hover:text-white">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#128C4A] shrink-0">
                <svg viewBox="0 0 32 32" className="w-4 h-4" fill="#fff" aria-hidden="true"><path d="M16.004 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.257.593 4.462 1.72 6.406L3.2 28.8l6.55-1.715a12.74 12.74 0 0 0 6.253 1.63h.005c7.06 0 12.8-5.74 12.8-12.8 0-3.42-1.332-6.635-3.75-9.053A12.72 12.72 0 0 0 16.004 3.2zm0 23.08h-.004a10.6 10.6 0 0 1-5.4-1.48l-.387-.23-4.017 1.053 1.072-3.916-.252-.402a10.56 10.56 0 0 1-1.62-5.626c0-5.867 4.774-10.64 10.647-10.64 2.843 0 5.515 1.108 7.525 3.12a10.57 10.57 0 0 1 3.116 7.526c0 5.867-4.773 10.64-10.64 10.64zm5.834-7.966c-.32-.16-1.892-.933-2.185-1.04-.293-.107-.507-.16-.72.16-.213.32-.826 1.04-1.013 1.253-.187.213-.373.24-.693.08-.32-.16-1.35-.498-2.572-1.587-.95-.848-1.593-1.895-1.78-2.215-.187-.32-.02-.493.14-.652.144-.143.32-.373.48-.56.16-.187.213-.32.32-.533.107-.213.053-.4-.027-.56-.08-.16-.72-1.734-.987-2.374-.26-.624-.524-.54-.72-.55l-.613-.01c-.213 0-.56.08-.853.4-.293.32-1.12 1.093-1.12 2.667 0 1.574 1.147 3.094 1.307 3.307.16.213 2.253 3.44 5.46 4.826.763.33 1.358.527 1.822.674.766.244 1.463.21 2.014.127.614-.092 1.892-.773 2.16-1.52.267-.747.267-1.387.187-1.52-.08-.133-.293-.213-.613-.373z" /></svg>
              </span>
              <span>For <strong className="text-orange-400">"RTSE"</strong> or <strong className="text-orange-400">"STUDYKIT"</strong> · <strong className="text-white whitespace-nowrap">721&nbsp;907&nbsp;4788</strong></span>
            </a>
          </div>
          <p className="break-normal">
            © {new Date().getFullYear()} The Rankers Academy. All Rights Reserved. Founded by VNIT Nagpur Alumni.
            {" · "}
            <Link to="/privacy-policy" data-testid="footer-privacy-policy" className="hover:text-amber-300 transition-colors underline">Privacy Policy</Link>
          </p>
        </div>

      </div>
    </footer>
  );
}
